import dayjs from 'dayjs';
import React from 'react';
import Geocomply from 'geocomply-web-client';

import { getGeocomplyLicense, decryptToken } from '../../api/geolocation';
import appConfig from '../../appConfig';
import { dispatchGeocomplyUpdate } from '../../utils/eventDispatchers';
import PortalSessionContext from '../PortalSessionContext/PortalSessionContext';
import geocomplyLicenseStore from './geocomplyLicenseStore';
import GeocomplyModal from './GeocomplyModal';
import { parseApiError } from '../../utils/parseApiError';

const geocomplyEnabled = appConfig.geocomply.enabled;
const GeocomplyClient = Geocomply.Client;
const geolocationDebounceInterval = 5000; //ms

const GeocomplyContext = React.createContext<{
	deleteLicense: () => void;
	expireLicense: () => void;
	expireToken: () => void;
	geocomplyLicense: GeocomplyState['license'];
	geolocationToken: GeocomplyState['token'];
	geolocationError: GeocomplyState['error'];
	hasErrors: boolean;
	hideModal: () => void;
	requestGeolocation: (reason: string) => void;
	requestPending: GeocomplyState['requestPending'];
	sdkState: SDKState;
	showModal: () => void;
}>({
	deleteLicense: () => undefined,
	expireLicense: () => undefined,
	expireToken: () => undefined,
	geocomplyLicense: null,
	geolocationToken: null,
	geolocationError: null,
	hasErrors: false,
	hideModal: () => undefined,
	requestGeolocation: () => undefined,
	requestPending: false,
	sdkState: {
		connected: false,
		localClientState: 'unknown',
	},
	showModal: () => undefined,
});

interface GeocomplyState {
	error: APIError | null;
	geolocationError: APIError | null;
	license: GetGeolocationLicenseResponse['data'] | null;
	requestPending: boolean;
	token: GetGeolocationTokenResponse['data'] | null;
}

interface SDKState {
	connected: boolean;
	localClientState: 'unknown' | 'installed' | 'not-installed';
}

export const GeocomplyProvider: React.FC = ({ children }) => {
	const { user } = React.useContext(PortalSessionContext);
	const geocomplyParamsRef = React.useRef({
		license: '',
		userId: '',
	});
	const lastGeoRequestDate = React.useRef<dayjs.Dayjs>();
	// convert to string and make sure isLoggedin
	const userId = user?.id ? `${user.id}` : null;

	const [modalVisible, setModalVisible] = React.useState(false);
	const [sdkState, setSdkState] = React.useState<SDKState>({
		connected: false,
		localClientState: 'unknown',
	});
	const [geocomplyState, setGeocomplyState] = React.useState<GeocomplyState>({
		error: null,
		geolocationError: null, // this is the error we can "safely" display to the users
		license: null,
		requestPending: false,
		token: null,
	});

	const firstTimeGeolocationFailedShown = React.useRef(false);

	const updateGeocomplyLicense = React.useCallback(async (forceServerUpdate?: boolean) => {
		// console.log('Update license, force:', forceServerUpdate);

		try {
			// Try from local storage first:
			if (!forceServerUpdate) {
				const storedLicense = await geocomplyLicenseStore.getItem();
				// console.log('retrieved storedLicense', storedLicense);

				if (storedLicense) {
					const license = JSON.parse(storedLicense) as GetGeolocationLicenseResponse['data'];

					if (license.license && dayjs(license.expired_at).isAfter(dayjs()) && !forceServerUpdate) {
						geocomplyParamsRef.current.license = license.license;

						setGeocomplyState((oldState) => ({
							...oldState,
							license,
						}));
						return true;
					}

					// console.log('updatelicense: deleting stored license because invalid');
					geocomplyLicenseStore.deleteItem();
				}
			}

			// if stored license is expired or if there's none
			// console.log('updatelicense: getting a new license');

			const newLicenseResponse = await getGeocomplyLicense();

			if (newLicenseResponse?.data?.license) {
				geocomplyParamsRef.current.license = newLicenseResponse?.data?.license;

				setGeocomplyState((oldState) => ({
					...oldState,
					license: newLicenseResponse.data,
				}));

				// console.log('updatelicense: storing new license', newLicenseResponse.data);
				geocomplyLicenseStore.setItem(JSON.stringify(newLicenseResponse.data));
				return true;
			}
		} catch (e: any) {
			// if (e?.errors) console.log('updatelicense error:', parseApiError(e.errors));
			// else console.log('error getting a license', e);
		}

		return false;
	}, []);

	// effect to update license on mount
	React.useEffect(() => {
		if (!geocomplyEnabled) return;

		updateGeocomplyLicense();
	}, [updateGeocomplyLicense]);

	// Memoised function to request a new geolocation token
	const requestGeolocation = React.useCallback((reason: string = 'Not Set', force: boolean = false) => {
		if (!geocomplyEnabled) return;

		if (
			!force &&
			!!lastGeoRequestDate.current &&
			dayjs().diff(lastGeoRequestDate.current) < geolocationDebounceInterval
		) {
			// console.log('ignoring geolocation request', reason, dayjs().diff(lastGeoRequestDate.current));
			return;
		}

		// console.log('requestGeolocation, reason:', reason);

		lastGeoRequestDate.current = dayjs();

		setGeocomplyState((oldState) => ({
			...oldState,
			requestPending: true,
		}));

		// NOTE: license and userId must be set just before making a geolocation request, otherwise
		// the request will fail with code CLNT_ERROR_INVALID_LICENSE_FORMAT
		GeocomplyClient.setLicense(geocomplyParamsRef.current.license)
			.setUserId(geocomplyParamsRef.current.userId)
			.setGeolocationReason(reason)
			.requestGeolocation();
	}, []);

	// Setting up listeners for the geocomply client
	React.useEffect(() => {
		if (!geocomplyEnabled) return;

		GeocomplyClient.on('error', (errorCode, errorMessage) => {
			// console.log('geocomply error happened', errorCode, errorMessage);

			switch (errorCode) {
				case GeocomplyClient.CLNT_ERROR_LOCAL_SERVICE_UNAVAILABLE:
					// console.log('plc not installed error', GeocomplyClient.isConnected());
					setGeocomplyState((oldState) => ({
						...oldState,
						requestPending: false,
					}));

					setSdkState((oldState) => ({
						...oldState,
						localClientState: 'not-installed',
						connected: false,
					}));

					break;

				case GeocomplyClient.CLNT_ERROR_REQUEST_GEOLOCATION_IN_PROGRESS:
					// there is a request already in progress, ignoring last geolocation request
					// console.log('ignoring geolocation request as there is already one pending');
					break;

				case GeocomplyClient.CLNT_ERROR_LICENSE_EXPIRED:
				case GeocomplyClient.CLNT_ERROR_INVALID_LICENSE_FORMAT:
					// console.log('license expired/invalid');
					// Get a new licence and if successful do attempt another geolocation
					updateGeocomplyLicense(true).then((success) => {
						if (success) requestGeolocation('License updated', true);
					});
					setGeocomplyState((oldState) => ({
						...oldState,
						requestPending: false,
					}));

					break;

				default:
					// console.log('sdk unhandled error event', errorCode, errorMessage);
					setGeocomplyState((oldState) => ({
						...oldState,
						requestPending: false,
						geolocationError: {
							error: [
								`We are unable to verify your location, please try again or contact our customer service. (error code: ${errorCode})`,
							],
						},
					}));

					// since there was an unknown erorr and there is no event for geocomply disconnect,
					// here we are checking if it's actually still connected
					setSdkState((oldState) => ({
						...oldState,
						connected: GeocomplyClient.isConnected(),
					}));
			}
		});

		GeocomplyClient.on('connect', () => {
			// console.log('SDK client connected');
			requestGeolocation('Session Start');

			setSdkState((oldState) => ({
				...oldState,
				localClientState: 'installed',
				connected: true,
			}));
		});

		GeocomplyClient.on('geolocation', (res: string) => {
			// console.log('geolocation event', res);
			decryptToken(res)
				.then((res) => {
					// console.log('token decrypted:', res);

					setGeocomplyState((oldState) => ({
						...oldState,
						requestPending: false,
						token: res.data,
						geolocationError: null,
						error: null,
					}));

					setModalVisible(false);
				})
				.catch((res) => {
					if (res.errors) {
						// console.log('token error:', res.errors);
					}

					setGeocomplyState((oldState) => ({
						...oldState,
						requestPending: false,
						token: null,
						geolocationError: res?.errors ?? {
							error: ['Geolocation failed.'],
						},
					}));
				});
		});

		GeocomplyClient.on('my_ip_success', (res: string) => {
			// console.log('event my_ip_success', res);
			// calling this otherwise Geocomply will keep firing this event
			GeocomplyClient.ackMyIpSuccess();

			requestGeolocation('IP Changed', true);
		});

		// GeocomplyClient.on('my_ip_failure', (res) => {
		// 	console.log('event my_ip_failure', res);
		// });

		// GeocomplyClient.on('log', (res) => {
		// 	console.log('geocomply log event', res);
		// });

		return () => {
			GeocomplyClient.off('error');
			GeocomplyClient.off('connect');
			GeocomplyClient.off('geolocation');
			GeocomplyClient.off('log');
			GeocomplyClient.off('my_ip_success');
			GeocomplyClient.off('my_ip_failure');
		};
	}, [requestGeolocation, updateGeocomplyLicense]);

	// When the user logs in or out
	React.useEffect(() => {
		if (!geocomplyEnabled) return;

		if (userId) {
			// console.log('user signed in, starting Geocomply', userId);
			geocomplyParamsRef.current.userId = userId;

			GeocomplyClient.connect(appConfig.geocomply.installerKey, appConfig.geocomply.envID);
			GeocomplyClient.startMyIpService();
		}
		if (!userId) {
			// console.log('user disconnected, resetting state');
			// note that the license data is staying
			setGeocomplyState((oldState) => ({
				...oldState,
				error: null,
				geolocationError: null,
				requestPending: false,
				token: null,
			}));
			geocomplyParamsRef.current.userId = '';

			GeocomplyClient.stopMyIpService();
			GeocomplyClient.disconnect();

			setSdkState({
				connected: false,
				localClientState: 'unknown',
			});
		}
	}, [userId]);

	// Automatically show the geocomply modal the first time we get an error
	const hasErrors = !!geocomplyState.geolocationError;
	const plcNotInstalled = sdkState.localClientState === 'not-installed';
	React.useEffect(() => {
		if ((hasErrors || plcNotInstalled) && !firstTimeGeolocationFailedShown.current) {
			firstTimeGeolocationFailedShown.current = true;
			setModalVisible(true);
		} else firstTimeGeolocationFailedShown.current = false;
	}, [hasErrors, plcNotInstalled]);

	// Whenever there is a state update dispatch to client apps
	React.useEffect(() => {
		if (!geocomplyEnabled) return;

		dispatchGeocomplyUpdate();
	}, [geocomplyState, sdkState]);

	// Effect to handle the token refresh timer
	React.useEffect(() => {
		// do nothing if user is not logged in or if there is no token yet
		if (!userId || !geocomplyState.token || !geocomplyEnabled) return;

		let secondsToExpire = Math.round(dayjs(geocomplyState.token.expired_at).diff(dayjs()) / 1000);

		if (geocomplyState.token.buffer_time) secondsToExpire -= geocomplyState.token.buffer_time;
		else secondsToExpire *= 0.9;

		// console.log('setting up timer to update geocomply token', secondsToExpire);

		const timeoutRef = setTimeout(() => {
			// console.log('token expired, requesting a new one');
			requestGeolocation('Token expiring');
		}, secondsToExpire * 1000);

		return () => {
			// console.log('clearing token expired timeout');
			clearTimeout(timeoutRef);
		};
	}, [geocomplyState.token, requestGeolocation, userId]);

	const deleteLicense = React.useCallback(() => {
		// console.log('deleting license');
		geocomplyLicenseStore.deleteItem().then(() => {
			setGeocomplyState((oldState) => ({
				...oldState,
				license: null,
				error: null,
				token: null,
			}));
		});
	}, []);

	// for dev use only: replace current license with an expired one
	const expireLicense = React.useCallback(() => {
		console.log('replacing current license with an expired one');

		// hardcoded expired license:
		const newLicense = {
			license:
				'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',
			expired_at: '2023-05-31T23:11:39Z',
		};

		geocomplyParamsRef.current.license = newLicense.license;

		geocomplyLicenseStore.setItem(JSON.stringify(newLicense)).then(() => {
			setGeocomplyState((oldState) => ({
				...oldState,
				license: newLicense,
			}));
		});
	}, []);

	const expireToken = React.useCallback(() => {
		// console.log('replacing current token with an expired one');

		const newToken = {
			id: 119,
			user_id: 100,
			token: 'Z7Xe7kLheCb7Uq21rkJAyMP5OGdWjkHxG4P48ws/yWCXfN77WvBPg8sPGrshG21B',
			created_at: '2022-10-07T01:17:30+00:00',
			expired_at: '2022-10-07T01:37:30+00:00',
			buffer_time: 35,
		};

		setGeocomplyState((oldState) => ({
			...oldState,
			token: newToken,
		}));
	}, []);

	const showModal = React.useCallback(() => setModalVisible(true), []);

	const hideModal = React.useCallback(() => setModalVisible(false), []);

	return (
		<GeocomplyContext.Provider
			value={{
				deleteLicense,
				expireLicense,
				expireToken,
				geocomplyLicense: geocomplyState.license,
				geolocationError: geocomplyState.geolocationError,
				geolocationToken: geocomplyState.token,
				hideModal,
				requestGeolocation,
				hasErrors: hasErrors || plcNotInstalled,
				requestPending: geocomplyState.requestPending,
				sdkState,
				showModal,
			}}
		>
			{children}
			{geocomplyEnabled && (
				<GeocomplyModal
					close={hideModal}
					error={geocomplyState.geolocationError}
					requestPending={geocomplyState.requestPending}
					plcInstallUrl={generatePLCInstallUrl()}
					plcNotInstalled={plcNotInstalled}
					requestGeolocation={() => {
						if (!sdkState.connected)
							GeocomplyClient.connect(appConfig.geocomply.installerKey, appConfig.geocomply.envID);
						else requestGeolocation('Retry Button');
					}}
					visible={modalVisible}
				/>
			)}
		</GeocomplyContext.Provider>
	);
};

export default GeocomplyContext;

const generatePLCInstallUrl = () => {
	const userAgent = window.navigator.userAgent;
	let os = 'unsupported';

	if (userAgent.includes('Windows')) os = 'win';
	else if (userAgent.includes('Mac')) os = 'mac';
	else return null; //not supported or unrecognised OS

	return `https://${appConfig.geocomply.envID === 'staging' ? 'stg-ums' : 'ums'}.geocomply.net/installer/url?id=${
		appConfig.geocomply.installerKey
	}&os=${os}&version=${appConfig.geocomply.sdkVersion}`;
};
